import { useState, useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Server } from "lucide-react";
import { Link } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { portalFetch } from "@/lib/portalApi";
import { useTargetTypePropertyDefinitions } from "@/hooks/useConnectorPropertyDefinitions";
import { autoSubscribeConnector } from "./ConnectorCreateForm";
import { CreateRecordTemplates } from "./CreateRecordTemplates";
import {
  applyPlannedRecords,
  validatePlannedRecords,
  type PlannedRecord,
} from "./createRecordTemplates.helpers";
import type { MappingTemplate } from "@/lib/portalData";

import {
  SqlConnectionWithTest,
  type SqlConnectionValues as TestableSqlConnectionValues,
} from "./SqlConnectionWithTest";
import { TargetTypeSelector, type TargetType } from "./TargetTypeSelector";
import { AuthorizedConnectionSelector } from "./AuthorizedConnectionSelector";
import { FtpConfigFields, type FtpConnectionValues } from "./FtpConfigFields";
import { ConnectorPropertyFields } from "./ConnectorPropertyFields";
import {
  missingRequiredTargetDef,
  requiredTargetDefs,
  targetPropertiesPayload,
} from "./targetCreateFields";
import { SelfHostTargetConfig } from "./SelfHostTargetConfig";
import { SchemaValidation } from "./SchemaValidation";

const DEFAULT_SOURCE_SQL_VALUES: TestableSqlConnectionValues = {
  host: "",
  database: "",
  username: "",
  password: "",
  schema: "",
};

const DEFAULT_FTP_VALUES: FtpConnectionValues = {
  host: "",
  username: "",
  password: "",
  path: "",
  processedPath: "",
  filter: "",
  excludeFilter: "",
};

export type SqlConnectorSourceType = "SqlServer" | "MySql";

type SqlConnectorCreateFormProps = {
  /**
   * Source SQL flavour. Drives the `sourceConnectorType` sent to the DP
   * connector-create endpoint AND the `type` on test-connection /
   * discover-tables calls inside the form.
   */
  sourceType?: SqlConnectorSourceType;
};

export function SqlConnectorCreateForm({
  sourceType = "SqlServer",
}: SqlConnectorCreateFormProps = {}) {
  const SOURCE_TYPE = sourceType;
  const typeSlug = sourceType === "MySql" ? "mysql" : "sqlserver";
  const subscriptionKey = sourceType === "MySql" ? "mysql" : "sql_server";
  const navigate = useNavigate();
  const { selectedOrganisation, featureAccess } = usePortalData();
  const { token } = useAuth();

  // Form state
  const [connectorName, setConnectorName] = useState("");
  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<TargetType | null>(null);

  // Source SQL config (uses shared SqlConnectionWithTest component)
  const [sourceConfig, setSourceConfig] = useState<TestableSqlConnectionValues>(
    DEFAULT_SOURCE_SQL_VALUES,
  );
  const [sourceConnectionString, setSourceConnectionString] = useState<
    string | null
  >(null);
  const [sourceConnectionTested, setSourceConnectionTested] = useState(false);

  // Target FTP config (target SQL config is managed by SelfHostTargetConfig/SchemaValidation)
  const [targetFtpConfig, setTargetFtpConfig] =
    useState<FtpConnectionValues>(DEFAULT_FTP_VALUES);

  // Registry-driven target config, for target types with no hardcoded block
  // below (Asana, HubSpot, and every future one). Typed values keyed by
  // property name; the definitions come from the DP target registry.
  const [targetPropertyValues, setTargetPropertyValues] = useState<
    Record<string, string>
  >({});

  // Self-host mode (super admin only)
  const [useSelfHost, setUseSelfHost] = useState(false);
  const [selfHostConfig, setSelfHostConfig] = useState<{
    connectionString: string | null;
    schema: string;
    connectionTested: boolean;
    schemaValidated: boolean;
    schemaAvailable: boolean;
  }>({
    connectionString: null,
    schema: "",
    connectionTested: false,
    schemaValidated: false,
    schemaAvailable: false,
  });

  // Managed (non-self-hosted) schema validation. The source name is the
  // <source> part of the standard <org>-<source>-<yymm>-<nnn> schema name, e.g.
  // "shipshape" (web #1412): letters and digits only, so the name stays valid.
  const [managedSourceName, setManagedSourceName] = useState("");
  const [managedSchema, setManagedSchema] = useState("");
  const [managedSchemaValidation, setManagedSchemaValidation] = useState<{
    validated: boolean;
    available: boolean;
  }>({ validated: false, available: false });

  // For RequiresAuth targets
  const [referenceConnectorId, setReferenceConnectorId] = useState<
    number | null
  >(null);
  const [, setReferenceProperties] = useState<
    Record<string, string>
  >({});

  // Record-types step: templates to stamp on the connector at create time.
  const [plannedRecords, setPlannedRecords] = useState<PlannedRecord[]>([]);
  const [loadedTemplates, setLoadedTemplates] = useState<MappingTemplate[]>([]);

  // Submission state
  const [submitting, setSubmitting] = useState(false);

  // Whether the user can use self-host mode (super admin only)
  const canUseSelfHost = featureAccess.canAccessAdminDashboard;

  // SqlServer and FTP keep their hardcoded blocks below: those carry behaviour
  // the registry does not model (managed-vs-self-host schema; the FTP
  // processed/filter fields). Registry rendering is strictly ADDITIVE, for the
  // target types that have no block and today therefore render nothing at all.
  const targetHasHardcodedBlock =
    selectedTarget?.type === "SqlServer" || selectedTarget?.type === "FTP";
  const useRegistryTargetFields = Boolean(
    selectedTarget && !selectedTarget.requiresAuth && !targetHasHardcodedBlock,
  );

  const {
    definitions: targetDefinitions,
    loading: targetDefinitionsLoading,
    failed: targetDefinitionsFailed,
  } = useTargetTypePropertyDefinitions(
    selectedTarget?.type,
    useRegistryTargetFields,
  );

  const requiredTargetDefinitions = requiredTargetDefs(targetDefinitions);

  const handleTargetPropertyChange = useCallback((name: string, value: string) => {
    setTargetPropertyValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleTargetChange = (targetId: number, target: TargetType) => {
    setSelectedTargetId(targetId);
    setSelectedTarget(target);
    // Reset target-specific fields when changing type
    setReferenceConnectorId(null);
    setReferenceProperties({});
    // Planned record-templates are target-specific — a Xero Invoice template must
    // NOT survive a switch to FTP and get stamped on the new target.
    setPlannedRecords([]);
    // Same reasoning, and it matters more here because these are credentials: a
    // token typed for HubSpot must not be carried into a connector created
    // against Asana.
    setTargetPropertyValues({});
    // Reset self-host config when target changes
    setSelfHostConfig({
      connectionString: null,
      schema: "",
      connectionTested: false,
      schemaValidated: false,
      schemaAvailable: false,
    });
  };

  const handleSelfHostToggle = () => {
    setUseSelfHost(!useSelfHost);
    // Reset configs when toggling
    setManagedSourceName("");
    setManagedSchema("");
    setManagedSchemaValidation({ validated: false, available: false });
    setSelfHostConfig({
      connectionString: null,
      schema: "",
      connectionTested: false,
      schemaValidated: false,
      schemaAvailable: false,
    });
  };

  const handleManagedSchemaValidation = useCallback(
    (result: { validated: boolean; available: boolean }) => {
      setManagedSchemaValidation(result);
    },
    [],
  );

  const handleSelfHostConfigChange = useCallback(
    (config: {
      connectionString: string | null;
      schema: string;
      connectionTested: boolean;
      schemaValidated: boolean;
      schemaAvailable: boolean;
    }) => {
      setSelfHostConfig(config);
    },
    [],
  );

  const handleSourceConfigChange = (
    field: keyof TestableSqlConnectionValues,
    value: string,
  ) => {
    setSourceConfig((prev) => ({ ...prev, [field]: value }));
    // Invalidate tested connection when connection fields change
    if (field !== "schema") {
      setSourceConnectionString(null);
      setSourceConnectionTested(false);
    }
  };

  const handleSourceConnectionTested = useCallback(
    (result: {
      success: boolean;
      connectionString: string | null;
      existingSchemas: string[];
    }) => {
      setSourceConnectionTested(result.success);
      setSourceConnectionString(result.success ? result.connectionString : null);
    },
    [],
  );

  const handleTargetFtpConfigChange = (
    field: keyof FtpConnectionValues,
    value: string,
  ) => {
    setTargetFtpConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleAuthorizedConnectionChange = (
    connectorId: number,
    properties: Record<string, string>,
  ) => {
    setReferenceConnectorId(connectorId);
    setReferenceProperties(properties);
  };

  const validateForm = (): string | null => {
    if (!connectorName.trim()) {
      return "Connector name is required";
    }
    if (!selectedTargetId) {
      return "Please select a target type";
    }
    if (!sourceConfig.host || !sourceConfig.database) {
      return "Source server and database are required";
    }
    if (!sourceConnectionTested || !sourceConnectionString) {
      return "Please test the source database connection first";
    }
    if (!sourceConfig.schema) {
      return "Please select a source schema";
    }

    if (selectedTarget?.requiresAuth) {
      if (!referenceConnectorId) {
        return `Please select an authorized ${selectedTarget.type} connection`;
      }
    } else if (selectedTarget?.type === "SqlServer") {
      // Check if using self-host mode
      if (useSelfHost && canUseSelfHost) {
        if (!selfHostConfig.connectionTested) {
          return "Please test the database connection first";
        }
        if (
          !selfHostConfig.schemaValidated ||
          !selfHostConfig.schemaAvailable
        ) {
          return "Please validate the schema name before creating the connector";
        }
      } else {
        if (
          !managedSchemaValidation.validated ||
          !managedSchemaValidation.available
        ) {
          return "Please validate the schema name before creating the connector";
        }
      }
    } else if (selectedTarget?.type === "FTP") {
      if (!targetFtpConfig.host || !targetFtpConfig.username) {
        return "FTP host and username are required";
      }
    } else if (useRegistryTargetFields) {
      // An IN-FLIGHT read must block for the same reason a failed one does, and
      // this is easy to miss because the window is short: until the fetch
      // resolves, `definitions` is [] and there is nothing for the required
      // check below to find, so a submit during that window sends a
      // credential-less POST and 422s server-side. Narrower and more
      // recoverable than the failed-read case — the fields do appear a moment
      // later — but it is the same "validation finds nothing to enforce" defect
      // this branch exists to close.
      if (targetDefinitionsLoading) {
        return `Still loading the ${selectedTarget?.type} settings. Try again in a moment.`;
      }
      // A failed definitions read must BLOCK, not wave the connector through.
      // Degrading to "no fields, nothing to validate" would send a POST that
      // 422s server-side with nowhere to type the credential — the exact dead
      // end this whole change removes, silently restored during an outage.
      if (targetDefinitionsFailed) {
        return `Could not load the ${selectedTarget?.type} settings. Retry before creating this connector.`;
      }
      // Complain here rather than letting the backend 422. Same rule, said in
      // the place the user can act on it.
      const missing = missingRequiredTargetDef(
        targetDefinitions,
        targetPropertyValues,
      );
      if (missing) {
        return `${missing.display_name} is required`;
      }
    }

    // Any chosen record templates must be fully bound (source + required slots).
    const recordError = validatePlannedRecords(plannedRecords, loadedTemplates);
    if (recordError) return recordError;

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    if (!selectedOrganisation) {
      toast.error("No organisation selected");
      return;
    }

    setSubmitting(true);

    try {
      // Build properties based on target type
      // Source connection string comes from the tested connection (validated via SqlConnectionWithTest)
      const properties: Record<string, string> = {
        "source.connectionString": sourceConnectionString!,
        "source.schema": sourceConfig.schema || "dbo",
      };

      if (
        selectedTarget?.type === "SqlServer" &&
        !selectedTarget?.requiresAuth
      ) {
        // Check if using self-host mode with validated connection
        if (useSelfHost && canUseSelfHost && selfHostConfig.connectionString) {
          properties["target.connectionString"] =
            selfHostConfig.connectionString;
          properties["target.schema"] = selfHostConfig.schema;
        } else {
          // Managed database — no connection string, backend uses managed target
          properties["target.schema"] = managedSchema;
        }
      } else if (selectedTarget?.type === "FTP") {
        properties["ftp.host"] = targetFtpConfig.host;
        properties["ftp.username"] = targetFtpConfig.username;
        properties["ftp.password"] = targetFtpConfig.password;
        properties["ftp.path"] = targetFtpConfig.path;
        if (targetFtpConfig.processedPath)
          properties["ftp.processedPath"] = targetFtpConfig.processedPath;
        if (targetFtpConfig.filter)
          properties["ftp.filter"] = targetFtpConfig.filter;
        if (targetFtpConfig.excludeFilter)
          properties["ftp.excludeFilter"] = targetFtpConfig.excludeFilter;
      } else if (useRegistryTargetFields) {
        Object.assign(
          properties,
          targetPropertiesPayload(targetDefinitions, targetPropertyValues),
        );
      }

      const requestBody = {
        name: connectorName.trim(),
        sourceConnectorType: SOURCE_TYPE,
        targetConnectorId: selectedTargetId,
        referenceConnectorId: selectedTarget?.requiresAuth
          ? referenceConnectorId
          : undefined,
        properties,
      };

      const created = await portalFetch<{ Id: number; Name: string }>(
        `/api/v1/gateway/business2cloud/api/v1/connectors/org/${selectedOrganisation.organisationId}`,
        {
          token: token ?? undefined,
          method: "POST",
          body: requestBody,
        },
      );

      // Auto-subscribe: create per-instance item subscription for this source
      if (token) {
        await autoSubscribeConnector(
          token,
          selectedOrganisation.organisationId,
          subscriptionKey,
          created?.Id,
        );
      }

      // Stamp any chosen record templates (create_if_absent). Independent +
      // non-fatal to the create — a per-record failure surfaces in a toast and is
      // retryable from the connector's editor (which has column dropdowns).
      if (token && created?.Id && plannedRecords.length > 0) {
        const failed = await applyPlannedRecords(token, created.Id, plannedRecords);
        if (failed.length > 0) {
          toast.warning(
            `Connector created, but some record templates didn't apply — fix them in the editor: ${failed.join("; ")}`,
          );
        }
      }

      toast.success("Connector created successfully");
      navigate({
        to: "/portal/connectors/$typeSlug",
        params: { typeSlug },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  if (!selectedOrganisation) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        No organisation selected.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/portal/connectors/$typeSlug"
          params={{ typeSlug }}
          className="flex items-center gap-2 text-sm text-primary transition hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {sourceType === "MySql" ? "MySQL" : "SQL Server"} Connectors
        </Link>
      </div>

      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          Create SQL Server Connector
        </h1>
        <p className="mt-2 text-slate-600">
          Configure a new connector to sync data from a SQL Server source
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Connector Name */}
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">
            Connector Details
          </h2>
          <div className="mt-4 space-y-2">
            <Label htmlFor="connector-name">Connector Name</Label>
            <Input
              id="connector-name"
              placeholder="e.g., SQL to Xero for Client ABC"
              value={connectorName}
              onChange={(e) => setConnectorName(e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-slate-500">
              A descriptive name to identify this connector
            </p>
          </div>
        </div>

        {/* Target Configuration */}
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">
            Target Configuration
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Where should data from SQL Server be sent?
          </p>

          <div className="mt-6 space-y-6">
            <TargetTypeSelector
              organisationId={selectedOrganisation.organisationId}
              value={selectedTargetId}
              onChange={handleTargetChange}
              disabled={submitting}
            />

            {/* Target-specific configuration */}
            {selectedTarget && (
              <div className="mt-6 border-t border-slate-100 pt-6">
                {selectedTarget.requiresAuth ? (
                  <AuthorizedConnectionSelector
                    organisationId={selectedOrganisation.organisationId}
                    connectorType={selectedTarget.type}
                    value={referenceConnectorId}
                    onChange={handleAuthorizedConnectionChange}
                    disabled={submitting}
                  />
                ) : selectedTarget.type === "SqlServer" ? (
                  <>
                    {/* Self-host toggle (super admin only) */}
                    {canUseSelfHost && (
                      <div className="mb-6">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={handleSelfHostToggle}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                              useSelfHost
                                ? "bg-primary"
                                : "bg-slate-200"
                            }`}
                            role="switch"
                            aria-checked={useSelfHost}
                            disabled={submitting}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                useSelfHost ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </button>
                          <div className="flex items-center gap-2">
                            <Server className="h-4 w-4 text-slate-500" />
                            <Label className="text-sm font-medium text-slate-700">
                              Use Self-Hosted Database
                            </Label>
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-slate-500">
                          Enable this to connect to your own SQL Server instance
                          with validated schema management.
                        </p>
                      </div>
                    )}

                    {/* Show self-host config or standard config based on toggle */}
                    {useSelfHost && canUseSelfHost ? (
                      <SelfHostTargetConfig
                        sourceType={SOURCE_TYPE}
                        orgName={
                          selectedOrganisation.organisationName || connectorName
                        }
                        onConfigChange={handleSelfHostConfigChange}
                        disabled={submitting}
                      />
                    ) : (
                      <div className="space-y-6">
                        {/* Managed database info */}
                        <div className="rounded-xl border border-blue-100 bg-blue-50 p-3">
                          <p className="text-sm text-blue-700">
                            Target database is managed by Larasoft. Only the
                            schema name needs to be configured.
                          </p>
                        </div>

                        {/* Greyed-out connection fields */}
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-slate-400">
                              Target Server
                            </Label>
                            <Input
                              disabled
                              value="Managed by Larasoft"
                              className="bg-slate-50 text-slate-400"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-slate-400">
                              Target Database
                            </Label>
                            <Input
                              disabled
                              value="Managed by Larasoft"
                              className="bg-slate-50 text-slate-400"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-slate-400">Username</Label>
                              <Input
                                disabled
                                value="Managed"
                                className="bg-slate-50 text-slate-400"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-slate-400">Password</Label>
                              <Input
                                disabled
                                type="password"
                                value="managed"
                                className="bg-slate-50 text-slate-400"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Schema with validation */}
                        <div className="space-y-4 border-t border-slate-200 pt-6">
                          <div className="space-y-2">
                            <Label htmlFor="managed-source-name">
                              Source name
                            </Label>
                            <Input
                              id="managed-source-name"
                              placeholder="e.g., shipshape"
                              value={managedSourceName}
                              onChange={(e) =>
                                setManagedSourceName(
                                  e.target.value
                                    .toLowerCase()
                                    .replace(/[^a-z0-9]/g, "")
                                    .slice(0, 20),
                                )
                              }
                              disabled={submitting}
                            />
                            <p className="text-xs text-slate-500">
                              A short name for the source system. It becomes
                              part of the schema name below.
                            </p>
                          </div>
                          <SchemaValidation
                            value={managedSchema}
                            onChange={setManagedSchema}
                            useManagedDatabase
                            autoGenerate
                            sourceType={managedSourceName}
                            orgName={
                              selectedOrganisation?.organisationName ||
                              connectorName
                            }
                            onValidationChange={
                              handleManagedSchemaValidation
                            }
                            disabled={submitting}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : selectedTarget.type === "FTP" ? (
                  <FtpConfigFields
                    values={targetFtpConfig}
                    onChange={handleTargetFtpConfigChange}
                    disabled={submitting}
                  />
                ) : targetDefinitionsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading {selectedTarget.type} settings...
                  </div>
                ) : targetDefinitionsFailed ? (
                  <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                    Could not load the {selectedTarget.type} settings, so the
                    fields this connector needs are unknown. Reload the page and
                    try again — creating it now would fail.
                  </div>
                ) : requiredTargetDefinitions.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-500">
                      {selectedTarget.type} needs these to write data. They are
                      stored against the connector and can be changed later.
                    </p>
                    <ConnectorPropertyFields
                      definitions={requiredTargetDefinitions}
                      values={targetPropertyValues}
                      onChange={handleTargetPropertyChange}
                      disabled={submitting}
                    />
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Source Configuration */}
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">
            Source Configuration
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {sourceType === "MySql" ? "MySQL" : "SQL Server"} database to read
            data from
          </p>

          <div className="mt-6">
            <SqlConnectionWithTest
              label="Source"
              sourceType={SOURCE_TYPE}
              values={sourceConfig}
              onFieldChange={handleSourceConfigChange}
              onConnectionTested={handleSourceConnectionTested}
              disabled={submitting}
            />
          </div>
        </div>

        {/* Record types (optional): stamp mapping templates at create time */}
        {selectedTargetId !== null && selectedTarget?.type && (
          <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <Label>Record types (optional)</Label>
            <CreateRecordTemplates
              targetType={selectedTarget.type}
              value={plannedRecords}
              onChange={setPlannedRecords}
              onTemplatesLoaded={setLoadedTemplates}
            />
          </div>
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4">
          <Link
            to="/portal/connectors/$typeSlug"
            params={{ typeSlug }}
          >
            <Button type="button" variant="outline" disabled={submitting}>
              Cancel
            </Button>
          </Link>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Connector"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
